import { ImageOffIcon } from 'lucide-react';
import { useState, type JSX, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { ReleaseNoteCategory } from '@/lib/api/release-notes.js';

type MarkdownChildren = { children?: ReactNode };
type MarkdownAnchor = { href?: string; children?: ReactNode };

/**
 * Slim markdown component map for release-note bodies — a deliberate subset
 * of MessageBody's chat renderer without the streaming-caret machinery.
 * Every link opens in a new tab (release notes never deep-link app routes).
 */
export const markdownComponents = {
  h1: ({ children }: MarkdownChildren) => (
    <h1 className="mt-4 mb-2 text-base font-semibold text-foreground first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: MarkdownChildren) => (
    <h2 className="mt-4 mb-2 text-base font-semibold text-foreground first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }: MarkdownChildren) => (
    <h3 className="mt-3 mb-1.5 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h3>
  ),
  p: ({ children }: MarkdownChildren) => (
    <p className="my-2 leading-relaxed first:mt-0 last:mb-0">{children}</p>
  ),
  ul: ({ children }: MarkdownChildren) => (
    <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>
  ),
  ol: ({ children }: MarkdownChildren) => (
    <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>
  ),
  li: ({ children }: MarkdownChildren) => (
    <li className="leading-relaxed">{children}</li>
  ),
  strong: ({ children }: MarkdownChildren) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }: MarkdownChildren) => (
    <em className="italic">{children}</em>
  ),
  a: ({ href, children }: MarkdownAnchor) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:decoration-primary"
    >
      {children}
    </a>
  ),
  pre: ({ children }: MarkdownChildren) => (
    <pre className="my-2 overflow-x-auto rounded-lg border border-border bg-muted p-3 font-mono text-xs leading-relaxed text-foreground [&_code]:rounded-none [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  code: ({ children }: MarkdownChildren) => (
    <code className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
      {children}
    </code>
  ),
  img: ({ src, alt }: { src?: string; alt?: string }) => (
    <NoteImage src={src} alt={alt} />
  ),
};

/**
 * Media block for note images/GIFs: renders the image full-width and
 * rounded per the design; an unreachable URL falls back to the artifact's
 * media placeholder card instead of the browser's broken-image glyph.
 */
function NoteImage({
  src,
  alt,
}: {
  src?: string;
  alt?: string;
}): JSX.Element | null {
  const [broken, setBroken] = useState(false);
  if (!src) return null;
  if (broken) {
    return (
      <span
        className="wn-media-fallback"
        role="img"
        aria-label={alt || 'Media unavailable'}
      >
        <ImageOffIcon aria-hidden />
        <span>{alt || 'Media unavailable'}</span>
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt ?? ''}
      loading="lazy"
      className="mt-2 w-full rounded-lg border border-border"
      onError={() => setBroken(true)}
    />
  );
}

export const remarkPlugins = [remarkGfm];

/** Human-readable publish date; falls back to the raw string for an unparsable timestamp. */
export function formatPublishedDate(publishedAt: string): string {
  const time = Date.parse(publishedAt);
  if (Number.isNaN(time)) return publishedAt;
  return new Date(time).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Compact relative date: "Today" / "Yesterday" / "N days ago", else the full date. */
export function relativeDate(publishedAt: string): string {
  const time = Date.parse(publishedAt);
  if (Number.isNaN(time)) return publishedAt;
  const days = Math.floor((Date.now() - time) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatPublishedDate(publishedAt);
}

/**
 * Label per category (badge colours live in whats-new-archive.css). A note may
 * carry an unknown category from a newer server, so callers fall back to
 * rendering no badge (never index blind).
 */
export const CATEGORY_STYLE: Record<ReleaseNoteCategory, { label: string }> = {
  new: { label: 'New' },
  improved: { label: 'Improved' },
  fixed: { label: 'Fixed' },
  announcement: { label: 'Announcement' },
};

/** The markdown body of a release note. */
export function NoteMarkdown({ bodyMd }: { bodyMd: string }): JSX.Element {
  return (
    <div className="break-words text-sm text-foreground/90">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        components={markdownComponents}
      >
        {bodyMd}
      </ReactMarkdown>
    </div>
  );
}
